<script setup lang="ts">
import { ref, computed } from 'vue'

type FileObject = File | string | null

interface Props {
  dropzoneOptions?: Record<string, unknown>
  editable?: boolean
  image?: FileObject
  selectFileText?: string
  size?: number
}

const props = withDefaults(defineProps<Props>(), {
  dropzoneOptions: () => ({
    url: '/fake-url',
    autoProcessQueue: false,
    maxFilesize: 2,
    acceptedFiles: 'image/*',
  }),
  editable: true,
  selectFileText: 'Select file',
  size: 200,
})

const fileUploaderRef = ref(null)
const dropzoneKlass = ref<string[]>([
  'w-100',
  'h-100',
  'd-flex',
  'flex-column',
  'justify-content-center',
  'align-items-center',
  'cursor-pointer',
])
const selectedFile = ref<FileObject>(props.image)

const emit = defineEmits<{
  (e: 'fileAdded', file: File, uploader: unknown): void
  (e: 'fileRemoved'): void
}>()

const removeDropzoneKlass = (klass: string): void => {
  dropzoneKlass.value = dropzoneKlass.value.filter((k) => k !== klass)
}

const addDropzoneKlass = (klass: string): void => {
  if (!dropzoneKlass.value.includes(klass)) {
    dropzoneKlass.value.push(klass)
  }
}

const fileAdded = (file: File): void => {
  addDropzoneKlass('dropzone-file-added')
  selectedFile.value = file
  emit('fileAdded', file, fileUploaderRef.value)
}
const dragLeave = (): void => removeDropzoneKlass('dropzone-hover')
const dragOver = (): void => addDropzoneKlass('dropzone-hover')

const dropzoneActiveKlasses = computed(() => dropzoneKlass.value.join(' '))

const imagePreviewUrl = computed(() => {
  if (!selectedFile.value) return null

  if (typeof selectedFile.value === 'string') {
    return selectedFile.value
  } else {
    // It's a File object, create a blob URL
    return URL.createObjectURL(selectedFile.value)
  }
})

const removeSelectedFile = (): void => {
  selectedFile.value = null
  emit('fileRemoved')
}
</script>

<template>
  <div
    class="image-upload-box p-0"
    :style="{ width: `${props.size}px`, height: `${props.size}px` }"
  >
    <UeDropzone
      v-if="!selectedFile && editable"
      id="dropzone_image"
      :ref="fileUploaderRef"
      :useCustomSlot="true"
      :includeStyling="false"
      :class="dropzoneActiveKlasses"
      :options="dropzoneOptions"
      @vdropzone-drag-over="dragOver"
      @vdropzone-drop="dragLeave"
      @vdropzone-drag-leave="dragLeave"
      @vdropzone-drag-end="dragLeave"
      @vdropzone-file-added="fileAdded"
    >
      <div class="upload-button d-flex flex-column align-items-center align-self-center p-3">
        <i class="bi bi-file-image"></i>
        <div class="pt-2 text-center">
          <span class="font-weight-bold">{{ selectFileText }}</span>
        </div>
      </div>
    </UeDropzone>
    <div
      v-else-if="selectedFile"
      class="image-preview-container position-relative d-flex align-items-center justify-content-center w-100 h-100"
    >
      <img :src="imagePreviewUrl" alt="Selected image preview" class="image-preview w-100 h-100" />
      <div v-if="editable" class="image-overlay d-flex align-items-center justify-content-center">
        <button
          type="button"
          class="remove-image-btn btn btn-secondary d-flex align-items-center justify-content-center p-0 rounded"
          @click="removeSelectedFile"
          title="Remove image"
        >
          <i class="bi bi-trash"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="sass">
.image-upload-box
  border-radius: 1rem
  background: var(--ue-gray-container-bg)

#dropzone_image
  cursor: pointer

  .dz-success-mark, .dz-error-mark, .dz-preview
    display: none

.dropzone-hover
  border: 1px dashed var(--uePurple)
  background: rgb(236, 230, 247)

.upload-button
  color: var(--ue-gray-container-color)

  i
    font-size: 2rem
  span
    line-height: 1.3
    word-break: break-word
    white-space: normal

.image-preview-container
  border-radius: 1rem
  overflow: hidden

  &:hover .image-overlay
    opacity: 1

.image-preview
  object-fit: cover
  border-radius: 1rem

.image-overlay
  position: absolute
  top: 0
  left: 0
  right: 0
  bottom: 0
  background: rgba(0, 0, 0, 0.5)
  opacity: 0
  transition: opacity 0.2s ease
  border-radius: 1rem

.remove-image-btn
  width: 2.5rem
  height: 2.5rem

  i
    font-size: 1.2rem
</style>
